/*
    * Main Frontend Script *
*/

import { SupportedLanguage } from "../common";

const serverInjectedData = (window as any).serverInjectedData || {};

//* MARK: Language select navigation
// ----------------------------------------------------------------------------
const languageSelector = document.getElementById("navbar-language-selector");
if (languageSelector) {
    const pageLanguage = getPageLanguage();

    // Toggle dropdown visibility on click
    document.getElementById("language-dropdown-toggle")?.addEventListener('click', function() {
        const dropdown = languageSelector.querySelector(".language-dropdown");
        if (dropdown) dropdown.classList.toggle("active");
    });

    // Handle language option clicks
    const languageOptions = languageSelector.querySelectorAll(".language-dropdown .option") as NodeListOf<HTMLImageElement> | undefined;
    languageOptions?.forEach(option => {
        option.addEventListener('click', function() {
            const selectedLang = option.dataset.lang as SupportedLanguage;
            goToAlternateLanguagePage(selectedLang);    
        });
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(event) {
        const dropdown = document.querySelector(".language-select .language-dropdown");
        const toggle = document.getElementById("language-dropdown-toggle");
        if (dropdown && toggle) {
            if (!dropdown.contains(event.target as Node) && !toggle.contains(event.target as Node)) {
                dropdown.classList.remove("active");
            }
        }
    });
}

function goToAlternateLanguagePage(language: SupportedLanguage) {
    const currentUrl = new URL(window.location.href);
    const pathSegments = currentUrl.pathname.split('/').filter(segment => segment.length > 0);
    if (pathSegments[0] === 'en' || pathSegments[0] === 'fr') {
        pathSegments[0] = language;
    } else {
        pathSegments.unshift(language);
    }
    let path = pathSegments.join('/');
    if (path === 'en' || path === 'fr') path += '/';
    currentUrl.pathname = '/' + path;
    window.location.href = currentUrl.toString();
}


function getPageLanguage() : SupportedLanguage {
    if (serverInjectedData.meta?.language) {
        return serverInjectedData.meta.language;
    }
    const urlPath = window.location.pathname;
    if (urlPath.startsWith('/fr/')) return 'fr';
    if (urlPath.startsWith('/en/')) return 'en';
    // Check cookies
    const cookieMatch = document.cookie.match(/(?:^|; )preferred_language=(fr|en)(?:;|$)/);
    if (cookieMatch) {
        return cookieMatch[1] as SupportedLanguage;
    }
    // Default to English
    return 'en';
}

// Steam language mapping based on page language
function getSteamLanguage(pageLang: SupportedLanguage): string {
    switch (pageLang) {
        case 'fr': return 'french';
        case 'en': return 'english';
        default: return 'english';
    }
}

const steamStoreWidget = document.getElementById("steam-store-widget");
if (steamStoreWidget) {
    const pageLang = getPageLanguage();
    const steamLang = getSteamLanguage(pageLang);
    const iframe = steamStoreWidget.querySelector('iframe');
    if (iframe) {
        const src = iframe.getAttribute('src') || '';
        const newSrc = src.includes('?') ? `${src}&l=${steamLang}` : `${src}?l=${steamLang}`;
        iframe.setAttribute('src', newSrc);
    }
}

//* MARK: Highlight navigation
// ----------------------------------------------------------------------------
const navLinks = document.querySelectorAll('nav a') as NodeListOf<HTMLAnchorElement>;

if (navLinks && navLinks.length > 0) {
    const currentPath = getRawLinkPath(window.location.pathname);
    
    navLinks.forEach(link => {
        const linkPath = getRawLinkPath(link.href);
        if (linkPath === currentPath) {
            link.classList.add('active');
        } else {
            link.classList.remove('active');
        }
    });

    function getRawLinkPath(urlString: string) {
        const url = new URL(urlString, window.location.origin);
        const pathSegments = url.pathname.split('/').filter(segment => segment.length > 0);
        if (pathSegments[0] === 'en' || pathSegments[0] === 'fr') {
            pathSegments.shift();
        }
        return '/' + pathSegments.join('/');
    }
}

//* MARK: Auto image or video elements
// ----------------------------------------------------------------------------
document.querySelectorAll('[auto-image-or-video]').forEach(container => {
    const url = container.getAttribute('data-url');
    if (!url) return;
    const img = container.querySelector('img') as HTMLImageElement | null;
    const video = container.querySelector('video') as HTMLVideoElement | null;
    if (url.endsWith('.webm') || url.endsWith('.mp4')) {
        if (video) {
            video.src = url;
            video.hidden = false;
        }
        if (img) img.hidden = true;
    } else {
        if (img) {
            img.src = url;
            img.hidden = false;
        }
        if (video) video.hidden = true;
    }
});

//* MARK: Mobile navigation
// ----------------------------------------------------------------------------

// On-off navigation menu for mobile
document.getElementById("nav-burger-icon")?.addEventListener('click', function() {
    const navElement = document.querySelector("nav")!;
    navElement.classList.toggle("opened");
    const isOpened = navElement.classList.contains("opened");
    if (isOpened) {
        history.pushState({ navOpened: true }, '', window.location.href);
    }

    // Render #background-decorative-circle-top-right above menu when opened
    const backgroundCircleTopRight = document.querySelector("#background-decorative-circle-top-right") as HTMLElement | null;
    if (backgroundCircleTopRight) {
        if (isOpened) {
            backgroundCircleTopRight.style.zIndex = "1";
        } else {
            backgroundCircleTopRight.style.zIndex = "-1";
        }
    }
});

// Listen for back/forward navigation
window.addEventListener('popstate', function () {
    const navElement = document.querySelector("nav")!;
    if (navElement.classList.contains("opened")) {
        navElement.classList.remove("opened");
    }
});


// TODO: Apply to associations logos
//* MARK: Partners Scroll
// ----------------------------------------------------------------------------

const partnersScrollContainer = document.getElementById('partners-scroll-container') as HTMLElement | null;
const partnersScrollContent = document.getElementById('partners-scroll-content') as HTMLElement | null;
if (partnersScrollContainer && partnersScrollContent) {
    // Wait for all images to load before initializing scroll
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            waitForImagesAndInitScroll(partnersScrollContainer, partnersScrollContent);
        });
    } else {
        waitForImagesAndInitScroll(partnersScrollContainer, partnersScrollContent);
    }
}

async function waitForImagesAndInitScroll(container: HTMLElement, content: HTMLElement) {
    const images = content.querySelectorAll('img');
    const imagePromises = Array.from(images).map(img => {
        if (img.complete) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve());
            img.addEventListener('error', () => resolve()); // Resolve even on error to avoid hanging
        });
    });

    // Wait for all images to load, or timeout after 5 seconds
    await Promise.race([
        Promise.all(imagePromises),
        new Promise(resolve => setTimeout(resolve, 5000))
    ]);

    initPartnersScroll(container, content);
}

function initPartnersScroll(partnersScrollContainer: HTMLElement, partnersScrollContent: HTMLElement) {
    const containerWidth = partnersScrollContainer.offsetWidth;
    const contentWidth = partnersScrollContent.scrollWidth;

    if (contentWidth > (containerWidth - 120)) {
        // Duplicate partners for infinite scroll
        const currentLogos = partnersScrollContent.innerHTML;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;
        partnersScrollContent.innerHTML += currentLogos;

        // Apply scrolling animation via CSS
        const scrollDuration = (contentWidth / 100) * 8;
        partnersScrollContent.style.animation = `partners-scroll ${scrollDuration}s linear infinite`;
    }
}


//* MARK: Rel Keep Params
// ----------------------------------------------------------------------------

// Keep URL parameters for all links with rel="keep-params"
const keepParamsLinks = document.querySelectorAll('a[rel="keep-params"]') as NodeListOf<HTMLAnchorElement>;
if (keepParamsLinks.length > 0) {
    const currentParams = window.location.search;
    keepParamsLinks.forEach(link => {
        if (link.href) {
            const url = new URL(link.href);
            url.search += currentParams;
            link.href = url.toString();
        }
    });
}
const keepProductLinks = document.querySelectorAll('a[rel="keep-product"]') as NodeListOf<HTMLAnchorElement>;
if (keepProductLinks.length > 0) {
    const urlParams = new URLSearchParams(window.location.search);
    const product = urlParams.get('product');
    if (product === 'standard' || product === 'premium') {
        keepProductLinks.forEach(link => {
            if (link.href) {
                const url = new URL(link.href);
                url.searchParams.set('product', product);
                link.href = url.toString();
            }
        });
    }
}

//* MARK: Auto video iframe
// ----------------------------------------------------------------------------

document.querySelectorAll('[auto-iframe]').forEach(container => {
    const videoUrl = container.getAttribute('data-video-url');
    if (!videoUrl) return;
    const youtubeUrl = new URL(videoUrl);
    if (youtubeUrl.hostname === 'www.youtube.com' || youtubeUrl.hostname === 'youtu.be') {
        let videoId = '';
        if (youtubeUrl.hostname === 'www.youtube.com') {
            videoId = youtubeUrl.searchParams.get('v') || '';
        } else if (youtubeUrl.hostname === 'youtu.be') {
            videoId = youtubeUrl.pathname.slice(1);
        }
        if (videoId) {
            const iframe = document.createElement('iframe');
            iframe.width = '560';
            iframe.height = '315';
            iframe.src = `https://www.youtube.com/embed/${videoId}`;
            iframe.title = 'YouTube video player';
            iframe.frameBorder = '0';
            iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
            iframe.referrerPolicy = 'strict-origin-when-cross-origin';
            iframe.allowFullscreen = true;
            container.appendChild(iframe);
        }
    }
});

//* MARK: Auth page
// ----------------------------------------------------------------------------

const adminLoginForm = document.getElementById('admin-login-form') as HTMLFormElement | null;
if (adminLoginForm) {
    const errorMessage = document.getElementById('form-error');
    const successMessage = document.getElementById('form-success');

    adminLoginForm.addEventListener('submit', async function(event) {
        event.preventDefault();
        const usernameInput = document.getElementById('username-field') as HTMLInputElement | null;
        const passwordInput = document.getElementById('password-field') as HTMLInputElement | null;
        if (!usernameInput || !passwordInput) {
            console.warn("Username or password input not found in admin login form.");
            return;
        }
        
        usernameInput.disabled = true;
        passwordInput.disabled = true;
        const username = usernameInput.value;
        const password = passwordInput.value;
        const nextUrl = new URLSearchParams(window.location.search).get('next') || '/admin';

        try {
            const response = await fetch('/api/login-admin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            if (response.ok) {
                // Show success message and hide form
                document.getElementById('signin-form-container')?.setAttribute('hidden', 'true');
                successMessage?.removeAttribute('hidden');  
                // Redirect to next page after a short delay
                setTimeout(() => {
                    window.location.href = nextUrl;
                }, 1000);
            } else {
                // Show error message
                errorMessage?.removeAttribute('hidden');
            }
        } catch (e) {
            console.error("Error during admin login:", e);
            errorMessage?.removeAttribute('hidden');
        } finally {
            // Re-enable form inputs
            usernameInput.disabled = false;
            passwordInput.disabled = false;
        }
    });
}

//* MARK: Projects/games list
// ----------------------------------------------------------------------------


const mobileListContainer = document.getElementById('mobile-project-list-container') as HTMLElement | null;
const desktopListContainer = document.getElementById('desktop-project-list-container') as HTMLElement | null;

if (mobileListContainer && desktopListContainer)
{
    const desktopColumn1 = desktopListContainer.querySelector('.column-1')!;
    const desktopColumn2 = desktopListContainer.querySelector('.column-2')!;
    let isDesktopColumnsCreated = false;

    const mediaQuery = window.matchMedia('(max-width: 1050px)');

    function onResize() {
        const isMobile = mediaQuery.matches;
        mobileListContainer!.style.display = isMobile ? 'flex' : 'none';
        desktopListContainer!.style.display = isMobile ? 'none' : 'flex';
        
        if (!isDesktopColumnsCreated && !isMobile) {
            createDesktopColumns();
        }

        if (isMobile) {
            isDesktopColumnsCreated = false;
        }
    }

    // All nodes inside mobile list container as an array of cloned nodes
    const desktopProjectItems = Array.from(mobileListContainer.children).map(node => node.cloneNode(true) as HTMLElement);
    const mobileProjectItems = Array.from(mobileListContainer.children) as HTMLElement[];

    function createDesktopColumns() {
        let column1Height = 0;
        let column2Height = 0;
        
        // Distribute project items into two columns for desktop layout
        desktopProjectItems.forEach(item => {
            if (column1Height <= (column2Height + 150)) {
                desktopColumn1.appendChild(item);
                column1Height += item.offsetHeight;
            } else {
                desktopColumn2.appendChild(item);
                column2Height += item.offsetHeight;
            }
        });

        isDesktopColumnsCreated = true;
    }

    createDesktopColumns();
    window.addEventListener('resize', onResize);
    onResize();


    //* MARK: Tags filter
    // ----------------------------------------------------------------------------
    const projectsPageElement = document.getElementById('projects');
    if (projectsPageElement) {
        const tagButtons : HTMLElement[] = [];
        const projectCards = document.querySelectorAll('.project-card') as NodeListOf<HTMLElement>;
        const allTags = new Set<string>();
        projectCards.forEach(card => {
            const tags = card.dataset.tags ? card.dataset.tags.split(',').map(tag => tag.trim()) : [];
            card.dataset.tags = tags.join(','); // Ensure tags are trimmed and properly set in dataset
            tags.forEach(tag => allTags.add(tag));
            const cardTagsContainer = card.querySelector('.project-card-tags-container');
            if (cardTagsContainer) {
                tags.forEach(tag => {
                    const tagElement = document.createElement('p');
                    tagElement.classList.add('tag');
                    tagElement.textContent = "#" + tag;
                    cardTagsContainer.appendChild(tagElement);
                });
            }
        });

        const allTagsContainer = document.getElementById('all-tags-container');
        if (allTagsContainer) {
            allTags.forEach(tag => {
                const button = document.createElement('p');
                button.classList.add('tag');
                button.textContent = "#" + tag;
                button.dataset.tag = tag;
                allTagsContainer.appendChild(button);
                tagButtons.push(button);
            });
        }
        
        tagButtons.forEach(button => {
            // Toggle 'selected' class on click on tag buttons
            button.addEventListener('click', function() {
                if (button.classList.contains('selected')) {
                    button.classList.remove('selected');
                } else {
                    button.classList.add('selected');
                }
            });

            // Filter projects based on selected tags
            button.addEventListener('click', function() {
                const selectedTags = Array.from(document.querySelectorAll('.tag.selected')) as HTMLParagraphElement[];
                const selectedTagValues = selectedTags.map(tag => tag.dataset.tag);

                // Merge desktop and mobile projects array
                const allProjectCards = mobileProjectItems.concat(desktopProjectItems) as HTMLElement[];
                allProjectCards.forEach(card => {
                    const cardTags = card.dataset.tags ? card.dataset.tags.split(',') : [];
                    const hasAllMatchingTags = selectedTagValues.every(tag => cardTags.includes(tag!));
                    if (selectedTagValues.length === 0 || hasAllMatchingTags) {
                        card.style.display = '';
                    } else {
                        card.style.display = 'none';
                    }
                });
                if (desktopColumn1 && desktopColumn2) {
                    desktopColumn1.innerHTML = '';
                    desktopColumn2.innerHTML = '';
                    isDesktopColumnsCreated = false;
                    createDesktopColumns();
                }
            });
        });
    }
}
